<script lang="ts">
  import { t, translationExists } from "../stores/translations";
  import { price, pricesExist } from "../stores/prices";
  import ArrowImage from "../assets/icons/arrow.svg";
  import EmailImage from "../assets/icons/email.svg";
  import PersonalAdviceInformation from "./PersonalAdviceInformation.svelte";
  import { createEventDispatcher, onDestroy, onMount } from "svelte";
  import { advice } from "../stores/advice";
  import { bodyDimensions } from "../stores/bodyDimensions";
  import { locale } from "../stores/locale";
  import { questionnaire } from "../stores/questionnaire";
  import Button from "./shared/Button.svelte";
  import { ButtonType } from "../types/button-type";
  import Icon from "./shared/Icon.svelte";
  import {
    mattressCdnUrls,
    selectedMattressCdnUrls,
  } from "../data/mattressCdnUrls";
  import { breakpoints } from "../data/breakpoints";
  import { googleTagManagerStore } from "../stores/googleTagManager";
  import Modal from "./shared/Modal.svelte";
  import AdviceModalContent from "./AdviceModalContent.svelte";
  import { getNewMattressReason } from "../helpers/helpers";

  export let adviceType: string = "advice-primary";
  export let resetOnSubmit: boolean;
  export let className: string = "";

  let showTwoMattresses: boolean = false;
  let modalOpened: boolean = false;

  let rootElement: HTMLElement;

  const dispatch = createEventDispatcher();

  const imageMapping = {
    "one-person-mattress-two": mattressCdnUrls.images.onePersonTwo,
    "one-person-mattress-elite": mattressCdnUrls.images.onePersonElite,
    "one-person-mattress-elysium": mattressCdnUrls.images.onePersonElysium,
    "one-person-mattress-evolve": mattressCdnUrls.images.onePersonEvolve,
    "one-person-mattress-one": mattressCdnUrls.images.onePersonOne,
    "two-one-person-mattresses-two": mattressCdnUrls.images.onePersonTwo,
    "two-one-person-mattresses-elite": mattressCdnUrls.images.onePersonElite,
    "two-one-person-mattresses-elysium":
      mattressCdnUrls.images.onePersonElysium,
    "two-one-person-mattresses-evolve": mattressCdnUrls.images.onePersonEvolve,
    "two-one-person-mattresses-one": mattressCdnUrls.images.onePersonOne,
    "two-person-mattress-two": mattressCdnUrls.images.twoPersonTwo,
    "two-person-mattress-elite": mattressCdnUrls.images.twoPersonElite,
    "two-person-mattress-elysium": mattressCdnUrls.images.twoPersonElysium,
    "two-person-mattress-evolve": mattressCdnUrls.images.twoPersonEvolve,
    "two-person-mattress-one": mattressCdnUrls.images.twoPersonOne,
  };

  const selectedImageMapping = {
    "one-person-mattress-two":
      selectedMattressCdnUrls.images.onePersonTwoSelected,
    "one-person-mattress-elite":
      selectedMattressCdnUrls.images.onePersonEliteSelected,
    "one-person-mattress-elysium":
      selectedMattressCdnUrls.images.onePersonElysiumSelected,
    "one-person-mattress-evolve":
      selectedMattressCdnUrls.images.onePersonEvolveSelected,
    "one-person-mattress-one":
      selectedMattressCdnUrls.images.onePersonOneSelected,
    "two-one-person-mattresses-two":
      selectedMattressCdnUrls.images.onePersonTwoSelected,
    "two-one-person-mattresses-elite":
      selectedMattressCdnUrls.images.onePersonEliteSelected,
    "two-one-person-mattresses-elysium":
      selectedMattressCdnUrls.images.onePersonElysiumSelected,
    "two-one-person-mattresses-evolve":
      selectedMattressCdnUrls.images.onePersonEvolveSelected,
    "two-one-person-mattresses-one":
      selectedMattressCdnUrls.images.onePersonOneSelected,
    "two-person-mattress-two":
      selectedMattressCdnUrls.images.twoPersonTwoSelected,
    "two-person-mattress-elite":
      selectedMattressCdnUrls.images.twoPersonEliteSelected,
    "two-person-mattress-elysium":
      selectedMattressCdnUrls.images.twoPersonElysiumSelected,
    "two-person-mattress-evolve":
      selectedMattressCdnUrls.images.twoPersonEvolveSelected,
    "two-person-mattress-one":
      selectedMattressCdnUrls.images.twoPersonOneSelected,
  };

  const intersectionObservers: Array<IntersectionObserver> = [];

  const mattresses = Object.keys($advice[adviceType]);

  let itemList: HTMLOListElement;
  let activeItemIndex: number = 0;

  onMount(() => setShowTwoMattresses());

  onDestroy(() => {
    intersectionObservers.forEach((intersectionObserver) =>
      intersectionObserver.disconnect(),
    );
  });

  function openModal(): void {
    if ($googleTagManagerStore.gtmActive) {
      googleTagManagerStore.openEmailPopup();
    }

    modalOpened = true;
  }

  function getAdviceRelevanceSlug(index: number): string {
    const adviceRelevance = ["best-choice", "sharp-price", "step-extra"];

    return adviceRelevance[index];
  }

  function getItemHeaderTranslationKey(index: number): string {
    return `advice.${getAdviceRelevanceSlug(index)}`;
  }

  function actionWhenInViewport(e, index): void {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          activeItemIndex = index;
        }
      },
      {
        root: itemList,
        rootMargin: "100% 0% 100% 0%",
        threshold: 0.55,
      },
    );

    observer.observe(e);

    intersectionObservers.push(observer);
  }

  function scrollView(direction: string): void {
    direction === "right" ? activeItemIndex++ : activeItemIndex--;
    const elementWidth = (itemList.childNodes[activeItemIndex] as HTMLLIElement)
      .offsetWidth;

    if (window.innerWidth < breakpoints.desktop) {
      itemList.parentElement.scrollBy({
        top: 0,
        left: direction === "right" ? elementWidth : -elementWidth,
        behavior: "smooth",
      });
    } else {
      setTimeout(() => 
        rootElement.scrollIntoView({
          behavior: "smooth",
          block: "end"
        })
      )
    }
  }

  function submitAdvice(e: Event, mattress: string): void {
    const action = window.location.host.includes(
      import.meta.env.VITE_CONFIGURATOR_URL,
    )
      ? "add-to-configuration"
      : "show-mattress";

    let bodyTypePartnerValue = null;
    let firmnessPartner = null;

    if ($advice["advice-partner"]) {
      bodyTypePartnerValue =
        $advice["advice-partner"][mattress]["body-type"].value;
      firmnessPartner =
        $advice["advice-partner"][mattress]["length-weight"].value;
    }

    const bodyType = $advice["advice-primary"][mattress]["body-type"].value;
    const firmness = $advice["advice-primary"][mattress]["length-weight"].value;
    const mattressType = $advice["mattress-type"];

    let eventDetails = {
      "advice-type": adviceType,
      mattress,
      "mattress-type": mattressType,
      "body-type": bodyType,
      "body-type-partner": bodyTypePartnerValue,
      firmness: firmness,
      "firmness-partner": firmnessPartner,
    };

    e.target.dispatchEvent(
      new CustomEvent("questionnaire-submit", {
        detail: eventDetails,
        composed: true,
      }),
    );

    if ($googleTagManagerStore.gtmActive) {
      googleTagManagerStore.selectMattress(
        Object.assign(eventDetails, {
          "new-mattress-reason": getNewMattressReason(
            $advice["new-mattress-reason"],
          ),
          relevance: $advice[adviceType][mattress]["budget"].value,
          action,
        }),
      );
    }

    dispatch("questionnairesubmit", $advice["advice-type"]);

    if (resetOnSubmit) {
      advice.resetAdvice();
      bodyDimensions.reset();
      questionnaire.init();
    }
  }

  function getAdviceButtonTranslationkey(): string {
    if (window.location.host.includes(import.meta.env.VITE_CONFIGURATOR_URL)) {
      return "advice.add-to-configuration";
    }

    return "advice.show-mattress";
  }

  function determineMattressType(mattressType): string {
    return !!$advice["advice-partner"] &&
      mattressType === "two-one-person-mattresses"
      ? "one-person-mattress"
      : mattressType;
  }

  function checkTwoPersonWithPartner(): boolean {
    return (
      !!$advice["advice-partner"] &&
      $advice["mattress-type"] === "two-person-mattress"
    );
  }

  function setShowTwoMattresses(): void {
    showTwoMattresses =
      $advice["mattress-type"] === "two-one-person-mattresses" &&
      !$advice["advice-partner"];
  }
</script>
<div class="advice-wrapper {className}" bind:this={rootElement}>
  <div class="advice-header">
    {#if $advice && adviceType === "advice-primary"}
      <h1>
        {$t("advice.our-advice")}
      </h1>
    {/if}

    {#if $advice && $advice["advice-partner"] && $advice["mattress-type"] !== "two-person-mattress"}
      <h2>
        {$t(
          adviceType === "advice-primary"
            ? "advice.header.for-you"
            : "advice.header.for-your-partner",
        )}
      </h2>
    {/if}
  </div>

  <section>
    <ol
      bind:this={itemList}
      class:two-person-mattress-advice={checkTwoPersonWithPartner()}
    >
      {#each mattresses as mattress, mattressIndex}
        {@const promoPrice = $price(
          mattress,
          determineMattressType($advice["mattress-type"]),
          true,
        )}

        {@const price = $price(
          mattress,
          determineMattressType($advice["mattress-type"]),
        )}
        <li
          use:actionWhenInViewport={mattressIndex}
          style:grid-area={`item-${mattressIndex}`}
          class:primary-advice={mattressIndex === 0}
          class:selected-advice={mattressIndex === activeItemIndex}
        >
          <p class="item-header">
            {$t(getItemHeaderTranslationKey(mattressIndex))}
          </p>

          <div
            class="price-container"
            class:secondary-advice={mattressIndex !== 0}
            class:promo-price={promoPrice}
          >
            <h2>
              {$t("advice.mattress")}

              <span>{mattress}</span>
            </h2>

            {#if !!$pricesExist && promoPrice}
              <span class="from-old-price">
                {$t("advice.price-from")}

                <span class="old-price">{price}</span>

                <span class="new-price">{promoPrice}</span>

                {#if $locale.includes("be")}*{/if}
              </span>
            {:else if !!$pricesExist && $advice}
              <span>
                {$t("advice.price-from")}

                {price}

                {#if $locale.includes("be")}*{/if}
              </span>
            {/if}
          </div>

          <div class="information-container">
            <div class="image-container">
              {#if showTwoMattresses}
                <div class="mattress-number">
                  <p>{$t("advice.two-pieces")}</p>
                </div>
              {/if}
              {#if mattressIndex === 0}
                <img
                  src={selectedImageMapping[
                    `${$advice["mattress-type"]}-${mattress}`
                  ]}
                  alt="advice mattress"
                />
              {:else}
                <img
                  src={imageMapping[`${$advice["mattress-type"]}-${mattress}`]}
                  alt="advice mattress"
                />
              {/if}
            </div>

            <div class="advice-information advice-information-primary">
              <PersonalAdviceInformation
                {adviceType}
                {mattress}
                {mattressIndex}
                checkTwoPersonWithPartner={checkTwoPersonWithPartner()}
              />
            </div>

            {#if $advice && checkTwoPersonWithPartner()}
              <div class="advice-information advice-information-partner">
                <PersonalAdviceInformation
                  adviceType="advice-partner"
                  {mattress}
                  {mattressIndex}
                  checkTwoPersonWithPartner={checkTwoPersonWithPartner()}
                />
              </div>
            {/if}
          </div>

          {#if $translationExists("advice.price-from-disclaimer")}
            <div class="disclaimer-container">
              <span>{$t("advice.price-from-disclaimer")}</span>
            </div>
          {/if}

          <div class="button-container">
            <Button
              outlined={true}
              type={mattressIndex === 0
                ? ButtonType.Primary
                : ButtonType.Secondary}
              on:click={(event) => submitAdvice(event, mattress)}
            >
              {$t(getAdviceButtonTranslationkey())}
            </Button>
          </div>
        </li>
      {/each}
    </ol>
  </section>

  <div
    class="navigation-container"
    class:two-person-mattress-advice={checkTwoPersonWithPartner()}
  >
    <div class="navigate-button button-left" class:visible={activeItemIndex > 0}>
      <Icon
        width="16px"
        height="14px"
        src={ArrowImage}
        alt="Arrow icon"
        on:click={(event) => scrollView("left")}
      />
    </div>

    <div class="navigation-items">
      {#each mattresses as _, index}
        <span
          class="navigation-item"
          class:selected={index === activeItemIndex}
        />
      {/each}
    </div>

    <div
      class:visible={activeItemIndex < mattresses.length - 1}
      class="navigate-button"
    >
      <Icon
        width="16px"
        height="14px"
        src={ArrowImage}
        alt="Arrow icon"
        on:click={(event) => scrollView("right")}
      />
    </div>
  </div>

  <div class="advice-button-container">
    <Button type={ButtonType.Secondary} outlined={true} on:click={openModal}>
      <div class="advice-button-content-container">
        <Icon src={EmailImage} alt="Email icon" />

        <p>{$t("save.advice-button")}</p>
      </div>
    </Button>
  </div>

  <Modal bind:opened={modalOpened} fullscreen={true}>
    <AdviceModalContent />
  </Modal>
</div>
<style lang="scss">
  @import "src/lib/styles/variables.scss";
  @import "src/lib/styles/mixins.scss";

.advice-wrapper {
  * {
    font-family: "Roboto";
    color: $color-text;
  }

  .advice-button-container {
    margin: 0 auto;
    padding: 0 1rem;
    max-width: 24.25rem;
  }

  .advice-button-content-container {
    display: flex;
    align-items: center;
    justify-content: center;

    & > p {
      line-height: 1;
      margin: 0 0 0 0.5rem;
      color: $color-primary;
    }
  }

  .navigation-container {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    width: auto;
    padding: 0 1rem;

    & > .navigation-items {
      display: flex;

      & > .navigation-item {
        display: block;
        width: 2rem;
        height: 4px;
        background-color: $color-disabled;
        margin: 0 0.125rem;

        &.selected {
          background-color: $color-primary;
        }
      }
    }

    & > .navigate-button {
      opacity: 0;
      pointer-events: none;
      background: transparent;
      width: fit-content;
      min-width: 48px;
      min-height: 48px;
      display: flex;
      justify-content: center;
      align-items: center;
      transition: opacity 80ms ease-in-out;

      &.visible {
        pointer-events: all;
        opacity: 1;
      }
    }

    & > .button-left {
      transform: rotateZ(180deg);
    }
  }

  .primary-advice,
  .primary-advice * {
    color: $color-white;
    background-color: $color-primary;

    & > .item-header {
      color: $color-white;
    }

    & > .image-container {
      & > .mattress-number {
        background-color: $color-white;

        & > p {
          color: $color-primary;
        }
      }
    }
  }

  .advice-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 1.5rem;

    & > h1 {
      font-family: "Roboto Slab";
      font-weight: $font-weight-bold;
      font-size: $font-size-extra-large;
      color: $color-text;
      width: fit-content;
      margin: 0;
    }

    & > h2 {
      font-family: "Roboto";
      font-weight: $font-weight-bold;
      font-size: $font-size-large;
      color: $color-primary;
      margin: 0.5rem 0 0;
      width: fit-content;
    }
  }

  section {
    width: inherit;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    margin-bottom: 1rem;

    &::-webkit-scrollbar {
      display: none;
    }

    & > ol {
      margin: 0;
      padding: 0;
      display: grid;
      grid-template-rows: 1.5rem 1fr 1.5rem;
      grid-template-columns: 1fr 1fr 1fr;
      grid-template-areas:
        ". . ."
        "item-0 item-1 item-2"
        ". . .";
      width: fit-content;
      scroll-snap-align: start;
      list-style: none;

      & > li {
        width: 80vw;
        margin: 0 0.5rem;
        padding: 2rem 1rem;
        background-color: $color-white;
        scroll-snap-align: center;
        border-radius: 8px;
        display: flex;
        flex-direction: column;

        &:first-of-type {
          margin-left: 1rem;
        }

        &:last-of-type {
          margin-right: 1rem;
        }

        & > .price-container {
          display: flex;
          align-items: center;
          width: fit-content;
          margin: 0 auto;
          line-height: 2.5rem;

          &.secondary-advice {
            * {
              color: $color-primary;
            }
          }

          &.promo-price {
            display: flex;
            flex-direction: column;

            span {
              line-height: 1rem;
            }
          }

          & > * {
            font-family: "Roboto";
            width: fit-content;
          }

          & > h2 {
            font-weight: $font-weight-normal;
            font-size: $font-size-large;
            margin: 0 0.5rem 0 0;

            & > span {
              text-transform: capitalize;
            }
          }

          & > span {
            font-weight: $font-weight-medium;
            font-size: $font-size-large;

            & > .old-price {
              text-decoration: line-through;
              font-size: $font-size-large;
            }

            & > .new-price {
              font-size: $font-size-medium;
            }
          }
        }

        & > .item-header {
          font-weight: $font-weight-bold;
          font-size: $font-size-extra-large;
          margin: 0 auto;
          width: fit-content;
          color: $color-afternoon-grey;
        }

        & > .information-container {
          margin-top: 1.5rem;

          & > .image-container {
            position: relative;

            & > .mattress-number {
              position: absolute;
              top: 40%;
              left: 60%;
              width: 3rem;
              height: 3rem;
              border-radius: 50%;
              background-color: $color-primary;
              text-align: center;
              font-size: $font-size-small;

              & > p {
                color: $color-white;
                background-color: transparent;
                padding: 0 0.5rem;
                font-weight: $font-weight-bold;
              }
            }

            & > img {
              width: 100%;
            }
          }

          & > .advice-information {
            margin-bottom: 2rem;
          }

          & > .advice-information-partner {
            padding-top: 2rem;
            border-top: 1px solid $color-secondary;

            &.primary-advice {
              border-top: 1px solid $color-white;
            }
          }
        }

        & > .button-container {
          width: -moz-available;
          width: -webkit-fill-available;
          width: fill-available;

          margin: 0 2rem;
          margin-top: auto;
        }

        & > .disclaimer-container {
          display: flex;
          justify-content: center;
          align-items: center;

          & > :last-child {
            margin-bottom: 1.5rem;
          }
        }
      }
    }
  }

  @include desktop {
    .advice-header {
      & > h1 {
        font-size: $font-size-mega-large;
        line-height: 1.33;
      }
    }

    section {
      width: inherit;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      margin-bottom: 3rem;

      & > ol {
        margin: 0;
        padding: 0;
        width: fit-content;
        scroll-snap-align: start;
        list-style: none;
        align-items: center;
        gap: 0 1rem;
        grid-template-areas:
          ". item-0 ."
          "item-1 item-0 item-2"
          ". item-0 .";

        &.two-person-mattress-advice {
          display: flex;

          & > li {
            display: none;

            &.selected-advice {
              display: flex;
              width: 69rem;
              padding: 2rem 3rem;
            }

            & > .price-container {
              margin-bottom: 0;
            }

            & > .price-container {
              flex-direction: row;
            }

            & > .information-container {
              display: flex;
              gap: 1.5rem;

              & > * {
                flex: 1;
              }

              & > .advice-information-primary {
                order: 1;
              }

              & > .advice-information-partner {
                order: 3;
                padding: 0;
                margin-top: 0;
                border: none;
              }

              & > .image-container {
                order: 2;
                align-self: center;
              }
            }

            & > .button-container {
              width: 20rem;
            }
          }
        }

        & > li {
          box-sizing: border-box;
          width: 24.25rem;
          box-shadow: 0px 4px 11px rgba(168, 168, 168, 0.25);
          border-radius: 8px;
          align-items: center;
          display: flex;
          flex-direction: column;
          padding-left: 2.5rem;
          padding-right: 2.5rem;
          margin: 0;
          height: 100%;

          & > .item-header {
            color: $color-afternoon-grey;
          }

          & > .price-container {
            margin-bottom: 1rem;

            & > h2,
            span {
              width: max-content;
            }
          }

          &:first-of-type {
            padding-top: 3.5rem;
            padding-bottom: 3.5rem;
          }

          & > img {
            height: 180px;
            width: auto;
          }
        }

        & > li:nth-child(1) {
          order: 2;
          margin: 0;
        }

        & > li:nth-child(3) {
          order: 3;
          margin: 0;
        }
      }
    }

    .navigation-container {
      display: none;
      margin: 0 auto;
      max-width: 34rem;

      &.two-person-mattress-advice {
        display: flex;
      }
    }
  }
}
</style>
